import React, { useState, useRef } from 'react';
import { getApiBase } from './config/apiBase';
import LyricAIPopup from './LyricAIPopup'; // Assuming LyricAIPopup is in the same directory
import RichTextEditor from './components/RichTextEditor';
import { authService } from './services/authService';
import './App.css'; // Import the CSS for left alignment

// Add keyframe animation for spinning
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);

const EpicForm = ({ onEpicCreated, selectedModel, selectedProject, userInfo }) => {
    const [summary, setSummary] = useState('');
    const [prompt, setPrompt] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);
    
    // Check if form should be enabled
    const isFormEnabled = !!selectedProject;

    // Helper function for authenticated API calls
    const authenticatedFetch = async (url, options = {}) => {
        try {
            const token = await authService.getAccessToken();
            const headers = {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                ...options.headers
            };
            
            return fetch(url, {
                ...options,
                headers
            });
        } catch (error) {
            console.error('Authentication error:', error);
            throw new Error('Failed to authenticate. Please log in again.');
        }
    };

    const [suggestedStories, setSuggestedStories] = useState([]);
    const [isSuggestingStories, setIsSuggestingStories] = useState(false);
    const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
    const [expandedStories, setExpandedStories] = useState({});
    const [useConfluenceForSuggestions, setUseConfluenceForSuggestions] = useState(false);
    const [suggestionsGenerated, setSuggestionsGenerated] = useState(false);
    const [currentSelectedModel] = useState(selectedModel);
    const [epicReviewResult, setEpicReviewResult] = useState(null);
    const [storyReviewResults, setStoryReviewResults] = useState({});
    const [isReviewingEpic, setIsReviewingEpic] = useState(false);
    const [reviewingStoryIndex, setReviewingStoryIndex] = useState(null);
    const [showCreateConfirm, setShowCreateConfirm] = useState(false);
    const [confirmData, setConfirmData] = useState(null);
    const storyDescriptionRefs = useRef({}); // Refs for each story description editor

    const handleGenerateDescription = async () => {
        if (!summary) return;

        setIsGeneratingDescription(true);
        setError(null);
        try {
            // Use absolute URL to ensure request goes to backend on port 3001
            const apiUrl = getApiBase();
            
            // Use the same rules-based endpoint as IssueForm for consistency
            const response = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    summary: summary,
                    issueType: 'Epic',
                    selectedModel: currentSelectedModel,
                    existingDescription: prompt && prompt.trim() ? prompt : null,
                }),
            });

            // Check if response is OK before parsing JSON
            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                let errorMessage = `Server responded with status ${response.status}`;
                
                // Try to get error details
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } else {
                    // Got HTML or other non-JSON response
                    const textResponse = await response.text();
                    console.error('Non-JSON response received:', textResponse.substring(0, 200));
                    errorMessage = response.status === 404 
                        ? 'API endpoint not found. Please ensure the backend server is running and the /api/ai/generate-description-from-rules endpoint is implemented.'
                        : 'Server error occurred. Please check the backend server logs.';
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();

            if (data.success) {
                // Combine user input with AI description
                let combinedDescription = '';
                if (data.userInput && data.userInput.trim()) {
                    // User had input - show both user input and AI enhancement
                    combinedDescription = `<p><b>📝 Your Input:</b></p>\n${data.userInput}\n<p><b>✨ AI Enhancement:</b></p>\n${data.description}`;
                } else {
                    // No user input, just show AI description
                    combinedDescription = data.description;
                }
                
                setPrompt(combinedDescription);
            } else {
                throw new Error(data.error || 'Failed to generate description.');
            }
        } catch (err) {
            console.error('Error generating description:', err);
            setError('Could not generate description. Please try again.');
        } finally {
            setIsGeneratingDescription(false);
        }
    };

    const handleGenerateStoryDescription = async (index) => {
        const story = suggestedStories[index];
        if (!story.summary) return;

        const newStories = [...suggestedStories];
        newStories[index].isGenerating = true;
        setSuggestedStories(newStories);

        try {
            const apiUrl = getApiBase();
            
            console.log(`🔄 Generating story description for: "${story.summary}"`);
            
            // Use the rules-based generation endpoint - EXACT SAME AS MAIN FORM
            const response = await authenticatedFetch(`${apiUrl}/api/ai/generate-description-from-rules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    summary: story.summary,
                    issueType: 'Story',
                    selectedModel: currentSelectedModel,
                    context: `This story is part of Epic: ${summary}\nEpic Description: ${prompt}`,
                    existingDescription: story.description && story.description.trim() ? story.description : null,
                }),
            });

            // Check if response is OK before parsing JSON
            if (!response.ok) {
                const contentType = response.headers.get('content-type');
                let errorMessage = `Server responded with status ${response.status}`;
                
                // Try to get error details
                if (contentType && contentType.includes('application/json')) {
                    const errorData = await response.json();
                    errorMessage = errorData.error || errorData.message || errorMessage;
                } else {
                    // Got HTML or other non-JSON response
                    const textResponse = await response.text();
                    console.error('Non-JSON response received:', textResponse.substring(0, 200));
                    errorMessage = response.status === 404 
                        ? 'API endpoint not found. Please ensure the backend server is running.'
                        : 'Server error occurred. Please check the backend server logs.';
                }
                throw new Error(errorMessage);
            }

            const data = await response.json();

            if (data.success) {
                console.log(`📝 [RECEIVED] AI description: ${data.description ? `${data.description.length} chars` : 'empty'}`);
                console.log(`📝 [RECEIVED] User input: ${data.userInput ? `${data.userInput.length} chars` : 'empty'}`);
                console.log(`📝 Updating state for story index: ${index}`);
                
                // Combine user input with AI description
                let combinedDescription = '';
                if (data.userInput && data.userInput.trim()) {
                    // User had input - show both user input and AI enhancement
                    combinedDescription = `<p><b>📝 Your Input:</b></p>\n${data.userInput}\n<p><b>✨ AI Enhancement:</b></p>\n${data.description}`;
                } else {
                    // No user input, just show AI description
                    combinedDescription = data.description;
                }
                
                // Create completely new array to force re-render
                const updatedStories = suggestedStories.map((s, idx) => {
                    if (idx === index) {
                        return {
                            ...s,
                            description: combinedDescription,
                            isGenerating: false
                        };
                    }
                    return s;
                });
                
                console.log(`� [AFTER UPDATE] New story description: ${updatedStories[index].description ? `${updatedStories[index].description.length} chars` : 'empty'}`);
                console.log(`📝 [AFTER UPDATE] isGenerating: ${updatedStories[index].isGenerating}`);
                
                setSuggestedStories(updatedStories);
                console.log(`✨ Story description regenerated using ${data.rulesApplied} rules with ${data.modelUsed}`);
            } else {
                throw new Error(data.error || 'Failed to generate description.');
            }
        } catch (err) {
            console.error('Error generating story description:', err);
            newStories[index].isGenerating = false;
            setSuggestedStories(newStories);
        }
    };

    const handleReviewEpicDescription = async () => {
        if (!prompt) {
            setEpicReviewResult({ isGood: false, message: 'No description to review.' });
            return;
        }

        setIsReviewingEpic(true);
        setEpicReviewResult(null); // Clear previous result

        try {
            const apiUrl = getApiBase();
            const reviewPrompt = `Review the following epic description and determine if it's ready for publishing as a JIRA ticket. Provide specific feedback on clarity, completeness, and any improvements needed. Start your response with YES if it's ready, or NO if it needs improvements:\n\n"${prompt}"`;
            
            const response = await authenticatedFetch(`${apiUrl}/api/ai/ask`, {
                method: 'POST',
                body: JSON.stringify({
                    message: reviewPrompt,
                    selectedModel: currentSelectedModel,
                }),
            });

            const data = await response.json();
            
            if (data.text) {
                const isGood = data.text.toLowerCase().includes('yes');
                // Remove the YES/NO from the beginning of the message
                let cleanedMessage = data.text.trim();
                cleanedMessage = cleanedMessage.replace(/^(YES|NO)[:\s]*/i, '');
                setEpicReviewResult({ isGood, message: cleanedMessage });
            }
        } catch (err) {
            console.error('Error reviewing epic description:', err);
            setEpicReviewResult({ isGood: false, message: 'Error reviewing description.' });
        } finally {
            setIsReviewingEpic(false);
        }
    };

    const handleReviewStoryDescription = async (index) => {
        const story = suggestedStories[index];
        if (!story.description) {
            setStoryReviewResults({ 
                ...storyReviewResults, 
                [index]: { 
                    status: 'needs_clarification',
                    rulesApplied: 'Story',
                    modelUsed: currentSelectedModel || 'Not specified'
                } 
            });
            return;
        }

        setReviewingStoryIndex(index);
        // Clear previous result for this story
        const newResults = { ...storyReviewResults };
        delete newResults[index];
        setStoryReviewResults(newResults);

        try {
            const apiUrl = getApiBase();
            
            const response = await authenticatedFetch(`${apiUrl}/api/story/review`, {
                method: 'POST',
                body: JSON.stringify({
                    summary: story.summary,
                    description: story.description,
                    issueType: 'Story',
                    selectedModel: currentSelectedModel,
                }),
            });

            const data = await response.json();
            
            if (data.success && data.review) {
                setStoryReviewResults({ ...storyReviewResults, [index]: data.review });
            } else {
                setStoryReviewResults({ 
                    ...storyReviewResults, 
                    [index]: { 
                        status: 'needs_clarification',
                        rulesApplied: 'Story',
                        modelUsed: currentSelectedModel || 'Not specified'
                    } 
                });
            }
        } catch (err) {
            console.error('Error reviewing story description:', err);
            setStoryReviewResults({ 
                ...storyReviewResults, 
                [index]: { 
                    status: 'needs_clarification',
                    rulesApplied: 'Story',
                    modelUsed: currentSelectedModel || 'Not specified'
                } 
            });
        } finally {
            setReviewingStoryIndex(null);
        }
    };

    const resetForm = () => {
        setSummary('');
        setPrompt('');
        setResult(null);
        setError(null);
        setLoading(false);
        setSuggestedStories([]);
        setExpandedStories({});
        setUseConfluenceForSuggestions(false);
        setSuggestionsGenerated(false);
    };

    const handleSuggestStories = async () => {
        if (!summary.trim() || !prompt.trim()) {
            setError("Please fill in the Epic Title and Description first.");
            return;
        }
        setIsSuggestingStories(true);
        setError(null);
        try {
            let confluenceContext = '';
            if (useConfluenceForSuggestions) {
                const confluenceResponse = await authenticatedFetch('/api/confluence/search', {
                    method: 'POST',
                    body: JSON.stringify({ query: summary }),
                });
                const confluenceData = await confluenceResponse.json();
                if (confluenceData.success && confluenceData.results.length > 0) {
                    confluenceContext = confluenceData.results.map(r => `Title: ${r.title}\nExcerpt: ${r.excerpt}`).join('\n\n');
                }
            }

            // Use absolute URL to ensure request goes to backend on port 3001
            const apiUrl = getApiBase();
            const response = await authenticatedFetch(`${apiUrl}/api/suggest-stories`, {
                method: 'POST',
                body: JSON.stringify({
                    summary,
                    description: prompt,
                    projectKey: selectedProject,
                    confluenceContext,
                    selectedModel: currentSelectedModel,
                }),
            });
            const data = await response.json();
            if (data.success) {
                const processedStories = data.stories.map(s => {
                    let criteria = [];
                    if (typeof s.acceptanceCriteria === 'string') {
                        criteria = s.acceptanceCriteria
                            .split('\n')
                            .map(line => line.trim().replace(/^\*\s*/, ''))
                            .filter(line => line);
                    } else if (Array.isArray(s.acceptanceCriteria)) {
                        criteria = s.acceptanceCriteria; // Already in correct format
                    }
                    return {
                        ...s,
                        acceptanceCriteria: criteria,
                        include: true,
                        isManual: false  // Mark AI-suggested stories
                    };
                });
                
                // Preserve any manually-created stories and append AI suggestions
                const manualStories = suggestedStories.filter(s => s.isManual);
                const allStories = [...manualStories, ...processedStories];
                
                setSuggestedStories(allStories);
                setExpandedStories(allStories.reduce((acc, _, index) => ({ ...acc, [index]: false }), {}));
                setSuggestionsGenerated(true);
                
                if (manualStories.length > 0) {
                    console.log(`✅ Preserved ${manualStories.length} manually-created stories and added ${processedStories.length} AI suggestions`);
                }
            } else {
                throw new Error(data.error || 'Failed to suggest stories.');
            }
        } catch (err) {
            console.error('Full error object:', err);
            setError(err.message);
        } finally {
            setIsSuggestingStories(false);
        }
    };

    const addNewStory = () => {
        const newStory = {
            summary: '',
            description: '',
            acceptanceCriteria: [''],
            include: true,
            isManual: true  // Mark this story as manually created so it won't be lost when suggesting
        };
        const newIndex = suggestedStories.length;
        setSuggestedStories(prev => [...prev, newStory]);
        setExpandedStories(prev => ({ ...prev, [newIndex]: true }));
    };

    const handleToggleStory = (index) => {
        setExpandedStories(prev => ({ ...prev, [index]: !prev[index] }));
    };

    const handleStoryInclusionChange = (index, include) => {
        const newStories = [...suggestedStories];
        newStories[index].include = include;
        setSuggestedStories(newStories);
    };

    const handleStorySummaryChange = (index, value) => {
        const newStories = [...suggestedStories];
        newStories[index].summary = value;
        setSuggestedStories(newStories);
    };

    const handleStoryDescriptionChange = (index, value) => {
        const newStories = [...suggestedStories];
        newStories[index].description = value;
        setSuggestedStories(newStories);
    };

    const handleStoryAcChange = (index, newAc) => {
        const newStories = [...suggestedStories];
        newStories[index].acceptanceCriteria = newAc;
        setSuggestedStories(newStories);
    };

    const handleRemoveStory = (index) => {
        const newStories = suggestedStories.filter((_, i) => i !== index);
        setSuggestedStories(newStories);
        // Update expanded stories state to account for removed index
        const newExpanded = {};
        Object.keys(expandedStories).forEach(key => {
            const numKey = parseInt(key);
            if (numKey < index) {
                newExpanded[numKey] = expandedStories[numKey];
            } else if (numKey > index) {
                newExpanded[numKey - 1] = expandedStories[numKey];
            }
        });
        setExpandedStories(newExpanded);
    };

    const allStoriesSelected = suggestedStories.every(s => s.include);

    const handleSelectAllChange = (e) => {
        const { checked } = e.target;
        const newStories = suggestedStories.map(story => ({ ...story, include: checked }));
        setSuggestedStories(newStories);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        
        const storiesToCreate = suggestedStories.filter(s => s.include);
        if (storiesToCreate.length === 0) {
            setError("Please select at least one story to create.");
            setLoading(false); // Stop loading if no stories are selected
            return;
        }

        // Confirm reporter before creating ticket
        const reporterInfo = {
          reporterName: userInfo?.name || 'Unknown',
          reporterEmail: userInfo?.email || null
        };
        
        // Store confirmation data and show modal
        setConfirmData({ ...reporterInfo, storiesToCreate });
        setShowCreateConfirm(true);
    };

    const confirmCreateEpic = async () => {
        if (!confirmData) return;
        
        const { storiesToCreate } = confirmData;
        setShowCreateConfirm(false);
        setConfirmData(null);

        try {
            // Use absolute URL to ensure request goes to backend on port 3001
            const apiUrl = getApiBase();
            
            const payload = {
                summary,
                description: prompt,
                projectKey: selectedProject,
                stories: storiesToCreate,
                selectedModel: currentSelectedModel,
                reporterEmail: userInfo?.email,
                reporterName: userInfo?.name,
            };
            
            console.log('=== PAYLOAD TO BACKEND (not JIRA) ===');
            console.log('Reporter Name (from Okta):', userInfo?.name);
            console.log('Reporter Email (from Okta):', userInfo?.email);
            console.log('Note: Backend will lookup JIRA accountId from this email');
            console.log('Full Payload to Backend:', JSON.stringify(payload, null, 2));
            console.log('Check SERVER TERMINAL for JIRA payload with accountId');
            console.log('=========================================');
            
            const response = await authenticatedFetch(`${apiUrl}/api/create-epic`, {
                method: 'POST',
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            
            // Log the backend response including JIRA payload details
            console.log('=== BACKEND RESPONSE ===');
            console.log('Success:', data.success);
            if (data.jiraPayload) {
                console.log('JIRA Payload (with accountId):', JSON.stringify(data.jiraPayload, null, 2));
            }
            if (data.error) {
                console.log('Error:', data.error);
            }
            console.log('========================');
            
            if (data.success) {
                setResult(data);
                if (onEpicCreated) {
                    onEpicCreated(data);
                }
            } else {
                throw new Error(data.error);
            }
        } catch (err) {
            console.error('Full error object:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="left-aligned epic-form-container" style={styles.container}>
            <div style={styles.header}>
                <h2 style={styles.title}>Create New Epic</h2>
                <p style={styles.subtitle}>Define an epic and let AI help you break it down into stories.</p>
                {!selectedProject && (
                    <div style={{ color: '#ff9800', marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff3e0', borderRadius: '4px' }}>
                        ⚠️ Please select a project to enable this form.
                    </div>
                )}
                {!userInfo?.email && (
                    <div style={{ color: '#ff9800', marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #ffa726' }}>
                        ℹ️ <strong>Note:</strong> Your email address could not be retrieved from Okta. Epics will be created with the API service account as reporter. For proper attribution, please refresh the page or log in again.
                    </div>
                )}
            </div>

            <form onSubmit={handleSubmit} style={styles.form}>
                <div style={styles.formGroup}>
                    <label htmlFor="epic-summary" style={styles.label}>Epic Title</label>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <input
                            id="epic-summary"
                            type="text"
                            value={summary}
                            onChange={(e) => setSummary(e.target.value)}
                            placeholder="e.g., Implement User Authentication"
                            style={styles.input}
                            disabled={!isFormEnabled}
                        />
                        <LyricAIPopup 
                            onInsert={(text) => setSummary(text)}
                            selectedModel={currentSelectedModel}
                            label="Get Epic Title"
                            style={{ marginLeft: '8px' }}
                            currentValue={summary}
                        />
                    </div>
                </div>

                <div style={styles.formGroup}>
                    <label htmlFor="epic-description" style={styles.label}>
                        Epic Description
                        {isGeneratingDescription && <span style={{ fontSize: '0.9em', color: '#666' }}> (Generating...)</span>}
                        {isReviewingEpic && <span style={{ fontSize: '0.9em', color: '#17a2b8' }}> (Reviewing...)</span>}
                    </label>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                        <textarea
                            id="epic-description"
                            value={prompt}
                            onChange={(e) => setPrompt(e.target.value)}
                            placeholder="A high-level overview of the epic's goals and scope."
                            style={{ ...styles.textarea, height: '120px', flex: 1 }}
                            disabled={!isFormEnabled || isReviewingEpic}
                        />
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                            <LyricAIPopup 
                                onInsert={(text) => setPrompt(text)}
                                selectedModel={currentSelectedModel}
                                label="Get Epic Description"
                                style={{ marginTop: '0px', marginLeft: '0px' }}
                                currentValue={prompt}
                                disabled={isReviewingEpic}
                            />
                            <button
                                type="button"
                                onClick={handleGenerateDescription}
                                disabled={!summary || isGeneratingDescription || !isFormEnabled || isReviewingEpic}
                                style={{
                                    ...styles.generateDescButton,
                                    opacity: (!summary || isGeneratingDescription || !isFormEnabled || isReviewingEpic) ? 0.6 : 1,
                                    cursor: (!summary || isGeneratingDescription || !isFormEnabled || isReviewingEpic) ? 'not-allowed' : 'pointer'
                                }}
                                title={isGeneratingDescription ? 'Generating description...' : 'Generate description from title'}
                            >
                                ✨
                            </button>
                            <button
                                type="button"
                                onClick={handleReviewEpicDescription}
                                disabled={!prompt || !isFormEnabled || isReviewingEpic}
                                style={{
                                    ...styles.generateDescButton,
                                    backgroundColor: isReviewingEpic ? '#6c757d' : '#17a2b8',
                                    opacity: (!prompt || !isFormEnabled || isReviewingEpic) ? 0.6 : 1,
                                    cursor: (!prompt || !isFormEnabled || isReviewingEpic) ? 'not-allowed' : 'pointer',
                                    animation: isReviewingEpic ? 'spin 1s linear infinite' : 'none'
                                }}
                                title={isReviewingEpic ? 'Reviewing description...' : 'Review description for ticket readiness'}
                            >
                                {isReviewingEpic ? '⏳' : '🔍'}
                            </button>
                        </div>
                    </div>
                    {epicReviewResult && (
                        <div style={{
                            marginTop: '8px',
                            padding: '12px',
                            backgroundColor: epicReviewResult.isGood ? '#d4edda' : '#fff3cd',
                            border: `1px solid ${epicReviewResult.isGood ? '#c3e6cb' : '#ffeeba'}`,
                            borderRadius: '4px',
                            color: '#000',
                            position: 'relative'
                        }}>
                            <button
                                type="button"
                                onClick={() => setEpicReviewResult(null)}
                                style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '18px',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0',
                                    width: '24px',
                                    height: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}
                                title="Close review"
                            >
                                ✕
                            </button>
                            <strong>Review Result:</strong>
                            <div style={{ marginTop: '4px', fontWeight: 'bold', fontSize: '1.05rem' }}>
                                Acceptable Jira Ticket: {epicReviewResult.isGood ? 'YES' : 'NO'}
                            </div>
                            <div style={{ marginTop: '8px', whiteSpace: 'pre-wrap', paddingRight: '24px' }}>{epicReviewResult.message}</div>
                        </div>
                    )}
                </div>

                {error && <div style={styles.error}>{error}</div>}

                <div style={styles.buttonGroup}>
                    <button
                        type="button"
                        onClick={handleSuggestStories}
                        disabled={!summary || !prompt || isSuggestingStories || !isFormEnabled || isReviewingEpic}
                        style={styles.buttonPrimary}
                    >
                        {isSuggestingStories ? 'Suggesting Stories...' : 'Suggest User Stories'}
                    </button>
                    <button
                        type="button"
                        onClick={addNewStory}
                        disabled={!isFormEnabled || isReviewingEpic}
                        style={{...styles.buttonPrimary, backgroundColor: '#28a745'}}
                        title="Add a new story manually"
                    >
                        ➕ Add Story
                    </button>
                    <button type="button" onClick={resetForm} style={styles.buttonSecondary} disabled={isReviewingEpic}>
                        Clear Form
                    </button>
                </div>
            </form>

            {suggestionsGenerated && (
                <div className="left-aligned" style={styles.suggestionsContainer}>
                    <div style={styles.suggestionsHeader}>
                        <h3 style={styles.suggestionsTitle}>Suggested Stories</h3>
                        <div style={styles.selectAllContainer}>
                            <input
                                type="checkbox"
                                id="select-all-stories"
                                checked={allStoriesSelected}
                                onChange={handleSelectAllChange}
                                style={{ marginRight: '0.5rem' }}
                            />
                            <label htmlFor="select-all-stories">Select All</label>
                        </div>
                    </div>

                    {suggestedStories.map((story, index) => (
                        <div key={index} style={{...styles.storyCard, textAlign: 'left'}}>
                            <div style={styles.storyHeader}>
                                <input
                                    type="checkbox"
                                    checked={story.include}
                                    onChange={(e) => handleStoryInclusionChange(index, e.target.checked)}
                                    style={{ marginRight: '1rem' }}
                                />
                                <h4 className="left-aligned story-title" style={{...styles.storyTitle, textAlign: 'left'}}>{story.summary || `New Story ${index + 1}`}</h4>
                                <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
                                    <button 
                                        onClick={() => handleRemoveStory(index)} 
                                        style={styles.removeStoryButton}
                                        title="Remove this story"
                                    >
                                        🗑️
                                    </button>
                                    <button onClick={() => handleToggleStory(index)} style={styles.toggleButton}>
                                        {expandedStories[index] ? '▼' : '◀'}
                                    </button>
                                </div>
                            </div>
                            {expandedStories[index] && (
                                <div style={styles.storyDetails}>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Story Title</label>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <input
                                                type="text"
                                                value={story.summary}
                                                onChange={(e) => handleStorySummaryChange(index, e.target.value)}
                                                style={{...styles.input, textAlign: 'left', flex: 1}}
                                            />
                                            <LyricAIPopup 
                                                onInsert={(text) => handleStorySummaryChange(index, text)}
                                                selectedModel={currentSelectedModel}
                                                label="Get Story Title"
                                                style={{ marginLeft: '0px' }}
                                                currentValue={story.summary}
                                            />
                                        </div>
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Description</label>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                                            <div style={{ flex: 1 }}>
                                                <RichTextEditor
                                                    ref={(el) => {
                                                        if (el) storyDescriptionRefs.current[index] = el;
                                                    }}
                                                    value={story.description}
                                                    onChange={(value) => handleStoryDescriptionChange(index, value)}
                                                    placeholder="Enter story description"
                                                    disabled={reviewingStoryIndex === index}
                                                />
                                            </div>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start' }}>
                                                <LyricAIPopup 
                                                    onInsert={(text) => handleStoryDescriptionChange(index, story.description + '<br/><br/>' + text)}
                                                    selectedModel={currentSelectedModel}
                                                    label="Get Story Description"
                                                    style={{ marginTop: '0px', marginLeft: '0px' }}
                                                    currentValue={story.description}
                                                    disabled={reviewingStoryIndex === index}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => handleGenerateStoryDescription(index)}
                                                    disabled={!story.summary || !isFormEnabled || reviewingStoryIndex === index}
                                                    style={{
                                                        ...styles.generateDescButton,
                                                        opacity: (!story.summary || !isFormEnabled || reviewingStoryIndex === index) ? 0.6 : 1,
                                                        cursor: (!story.summary || !isFormEnabled || reviewingStoryIndex === index) ? 'not-allowed' : 'pointer'
                                                    }}
                                                    title="Generate description from title"
                                                >
                                                    ✨
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => handleReviewStoryDescription(index)}
                                                    disabled={!story.description || !isFormEnabled || reviewingStoryIndex === index}
                                                    style={{
                                                        ...styles.generateDescButton,
                                                        backgroundColor: reviewingStoryIndex === index ? '#6c757d' : '#17a2b8',
                                                        opacity: (!story.description || !isFormEnabled || reviewingStoryIndex === index) ? 0.6 : 1,
                                                        cursor: (!story.description || !isFormEnabled || reviewingStoryIndex === index) ? 'not-allowed' : 'pointer',
                                                        animation: reviewingStoryIndex === index ? 'spin 1s linear infinite' : 'none'
                                                    }}
                                                    title={reviewingStoryIndex === index ? 'Reviewing description...' : 'Review description for ticket readiness'}
                                                >
                                                    {reviewingStoryIndex === index ? '⏳' : '🔍'}
                                                </button>
                                            </div>
                                        </div>
                                        {storyReviewResults[index] && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '16px',
                                                backgroundColor: '#f8f9fa',
                                                border: '1px solid #dee2e6',
                                                borderRadius: '4px',
                                                color: '#000',
                                                position: 'relative'
                                            }}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const newResults = { ...storyReviewResults };
                                                        delete newResults[index];
                                                        setStoryReviewResults(newResults);
                                                    }}
                                                    style={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        right: '8px',
                                                        background: 'transparent',
                                                        border: 'none',
                                                        fontSize: '18px',
                                                        cursor: 'pointer',
                                                        color: '#666',
                                                        padding: '0',
                                                        width: '24px',
                                                        height: '24px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center'
                                                    }}
                                                    title="Close review"
                                                >
                                                    ✕
                                                </button>
                                                <h4 style={{ marginTop: 0, marginBottom: '16px' }}>Review Result</h4>
                                                
                                                <div style={{ marginBottom: '12px' }}>
                                                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                                        Acceptable Jira Ticket: {' '}
                                                        <span style={{ 
                                                            color: storyReviewResults[index].status === 'approved' ? '#28a745' : '#dc3545',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            {storyReviewResults[index].status === 'approved' ? 'YES' : 'NO'}
                                                        </span>
                                                    </span>
                                                </div>

                                                <div style={{ marginBottom: '8px' }}>
                                                    <strong>JSON Template Used:</strong> {storyReviewResults[index].rulesApplied || 'Not Found From Server'}.json
                                                </div>

                                                <div style={{ marginBottom: '16px' }}>
                                                    <strong>AI Model Used:</strong> {storyReviewResults[index].modelUsed || 'Not specified'}
                                                </div>

                                                {/* Enhanced Rule Details */}
                                                {storyReviewResults[index].ruleDetails && Object.keys(storyReviewResults[index].ruleDetails).length > 0 && (
                                                    <div style={{ 
                                                        marginTop: '20px', 
                                                        paddingTop: '16px', 
                                                        borderTop: '2px solid #dee2e6' 
                                                    }}>
                                                        <h5 style={{ marginTop: 0, marginBottom: '12px', color: '#495057' }}>
                                                            Validation Rules Applied
                                                        </h5>
                                                        {Object.entries(storyReviewResults[index].ruleDetails).map(([ruleName, ruleData]) => (
                                                            <div key={ruleName} style={{ 
                                                                marginBottom: '16px',
                                                                padding: '12px',
                                                                backgroundColor: '#fff',
                                                                border: '1px solid #dee2e6',
                                                                borderRadius: '4px'
                                                            }}>
                                                                <h6 style={{ 
                                                                    marginTop: 0, 
                                                                    marginBottom: '8px',
                                                                    color: '#007bff',
                                                                    fontSize: '1rem'
                                                                }}>
                                                                    {ruleData.reportHeading}
                                                                    {ruleData.required && (
                                                                        <span style={{ 
                                                                            marginLeft: '8px',
                                                                            padding: '2px 6px',
                                                                            backgroundColor: '#dc3545',
                                                                            color: '#fff',
                                                                            fontSize: '0.75rem',
                                                                            borderRadius: '3px',
                                                                            fontWeight: 'normal'
                                                                        }}>
                                                                            REQUIRED
                                                                        </span>
                                                                    )}
                                                                </h6>
                                                                
                                                                {ruleData.industryStandards && (
                                                                    <div>
                                                                        <p style={{ 
                                                                            margin: '8px 0',
                                                                            fontSize: '0.9rem',
                                                                            color: '#495057'
                                                                        }}>
                                                                            <strong>Framework:</strong> {ruleData.industryStandards.framework}
                                                                            {ruleData.industryStandards.format && (
                                                                                <span> ({ruleData.industryStandards.format})</span>
                                                                            )}
                                                                        </p>
                                                                        
                                                                        {ruleData.industryStandards.description && (
                                                                            <p style={{ 
                                                                                margin: '8px 0',
                                                                                fontSize: '0.85rem',
                                                                                color: '#6c757d',
                                                                                lineHeight: '1.5'
                                                                            }}>
                                                                                {ruleData.industryStandards.description}
                                                                            </p>
                                                                        )}
                                                                        
                                                                        {ruleData.industryStandards.bestPractices && ruleData.industryStandards.bestPractices.length > 0 && (
                                                                            <details style={{ marginTop: '8px' }}>
                                                                                <summary style={{ 
                                                                                    cursor: 'pointer',
                                                                                    fontWeight: 'bold',
                                                                                    fontSize: '0.9rem',
                                                                                    color: '#495057',
                                                                                    marginBottom: '8px'
                                                                                }}>
                                                                                    Best Practices ({ruleData.industryStandards.bestPractices.length})
                                                                                </summary>
                                                                                <ul style={{ 
                                                                                    margin: '8px 0',
                                                                                    paddingLeft: '20px',
                                                                                    fontSize: '0.85rem',
                                                                                    color: '#6c757d'
                                                                                }}>
                                                                                    {ruleData.industryStandards.bestPractices.map((practice, idx) => (
                                                                                        <li key={idx} style={{ marginBottom: '4px' }}>{practice}</li>
                                                                                    ))}
                                                                                </ul>
                                                                            </details>
                                                                        )}
                                                                        
                                                                        {ruleData.industryStandards.references && ruleData.industryStandards.references.length > 0 && (
                                                                            <div style={{ 
                                                                                marginTop: '8px',
                                                                                fontSize: '0.8rem',
                                                                                color: '#6c757d'
                                                                            }}>
                                                                                <strong>References:</strong> {ruleData.industryStandards.references.join(', ')}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <div style={styles.formGroup}>
                                        <label style={styles.label}>Acceptance Criteria</label>
                                        <EditableList
                                            items={story.acceptanceCriteria}
                                            onChange={(newAc) => handleStoryAcChange(index, newAc)}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={addNewStory} style={{...styles.buttonSecondary, marginTop: '1rem'}}>
                        + Add New Story
                    </button>
                </div>
            )}

            {suggestionsGenerated && (
                <div style={{ ...styles.buttonGroup, marginTop: '2rem' }}>
                    <button
                        type="submit"
                        onClick={handleSubmit}
                        disabled={loading || suggestedStories.filter(s => s.include).length === 0}
                        style={styles.buttonPrimary}
                    >
                        {loading ? 'Creating Epic and Stories...' : `Create Epic & ${suggestedStories.filter(s => s.include).length} Stories`}
                    </button>
                </div>
            )}

            {result && (
                <div style={styles.result}>
                    <h3 style={styles.resultTitle}>Epic and Stories Created Successfully!</h3>
                    <p>
                        <strong>Epic:</strong> <a href={result.epicLink} target="_blank" rel="noopener noreferrer">{result.epicKey}</a> - {result.summary}
                    </p>
                    <ul>
                        {result.stories.map(story => (
                            <li key={story.key}>
                                <strong>Story:</strong> <a href={story.link} target="_blank" rel="noopener noreferrer">{story.key}</a> - {story.summary}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Custom Confirmation Modal */}
            {showCreateConfirm && confirmData && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 10000,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '8px',
                        padding: '2rem',
                        maxWidth: '500px',
                        width: '90%',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}>
                        <h3 style={{ marginTop: 0, marginBottom: '1rem', color: '#333' }}>
                            {confirmData.reporterEmail ? 'Create JIRA Epic with the following reporter?' : 'Create JIRA Epic?'}
                        </h3>
                        {confirmData.reporterEmail ? (
                            <div style={{ marginBottom: '1.5rem' }}>
                                <p style={{ margin: '0.5rem 0' }}><strong>Name:</strong> {confirmData.reporterName}</p>
                                <p style={{ margin: '0.5rem 0' }}><strong>Email:</strong> {confirmData.reporterEmail}</p>
                                <p style={{ color: '#666', marginTop: '1rem', fontSize: '0.9rem' }}>
                                    Note: If JIRA does not allow setting the reporter, the ticket will be created with the API service account as reporter.
                                </p>
                            </div>
                        ) : (
                            <div style={{ marginBottom: '1.5rem' }}>
                                <p style={{ color: '#ff9800', marginBottom: '1rem' }}>
                                    ⚠️ <strong>Warning:</strong> Your email address is not available from Okta.
                                </p>
                                <p style={{ color: '#666', fontSize: '0.9rem' }}>
                                    The ticket will be created with the API service account as reporter.
                                </p>
                            </div>
                        )}
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => {
                                    setShowCreateConfirm(false);
                                    setConfirmData(null);
                                    setLoading(false);
                                }}
                                style={{
                                    padding: '0.5rem 1.5rem',
                                    backgroundColor: '#f0f0f0',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmCreateEpic}
                                style={{
                                    padding: '0.5rem 1.5rem',
                                    backgroundColor: '#0052cc',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    fontWeight: '500'
                                }}
                            >
                                Create Epic
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const EditableList = ({ items, onChange }) => {
    const handleItemChange = (index, value) => {
        const newItems = [...items];
        newItems[index] = value;
        onChange(newItems);
    };

    const handleAddItem = () => {
        onChange([...items, '']);
    };

    const handleRemoveItem = (index) => {
        const newItems = items.filter((_, i) => i !== index);
        onChange(newItems);
    };

    return (
        <div>
            {items.map((item, index) => (
                <div key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <input
                        type="text"
                        value={item}
                        onChange={(e) => handleItemChange(index, e.target.value)}
                        style={{ flex: 1, marginRight: '0.5rem', ...styles.input }}
                    />
                    <button type="button" onClick={() => handleRemoveItem(index)} style={styles.removeButton}>
                        &times;
                    </button>
                </div>
            ))}
            <button type="button" onClick={handleAddItem} style={styles.addButton}>
                + Add Criterion
            </button>
        </div>
    );
};

export default EpicForm;

const styles = {
    container: {
        padding: '2rem',
        backgroundColor: '#fff',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        maxWidth: '1000px',
        margin: '0 auto',
        color: '#000',
        overflow: 'visible',
    },
    header: {
        marginBottom: '2rem',
    },
    title: {
        fontSize: '1.8rem',
        fontWeight: '700',
        margin: 0,
        color: '#0052cc',
    },
    subtitle: {
        fontSize: '1rem',
        color: '#555',
        marginTop: '0.5rem',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
    },
    formGroup: {
        marginBottom: '1rem',
    },
        inputGroup: {
            display: 'flex',
            flexDirection: 'column',
        },
        label: {
            display: 'block',
            fontWeight: '600',
            marginBottom: '0.25rem',
            color: '#000',
            textAlign: 'left',
        },
        input: {
            padding: '0.75rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '1rem',
            width: '100%',
            textAlign: 'left',
        },
        textarea: {
            padding: '0.75rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '1rem',
            minHeight: '150px',
            resize: 'vertical',
            width: '100%',
            textAlign: 'left',
            fontFamily: 'inherit',
        },
        checkboxGroup: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        button: {
            padding: '0.8rem 1.5rem',
            backgroundColor: '#0052cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'background-color 0.2s',
        },
        buttonDisabled: {
            backgroundColor: '#a5adba',
            cursor: 'not-allowed',
        },
        generateDescButton: {
            width: '30px',
            height: '30px',
            padding: '0',
            backgroundColor: '#6f42c1',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
        },
        result: {
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#e6f7ff',
            border: '1px solid #91d5ff',
            borderRadius: '4px',
        },
        error: {
            marginTop: '1.5rem',
            padding: '1rem',
            backgroundColor: '#fff1f0',
            border: '1px solid #ffa39e',
            borderRadius: '4px',
            color: '#cf1322',
        },
        storyListItem: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            marginBottom: '1rem',
            border: '1px solid #ddd',
            padding: '1rem',
            borderRadius: '4px',
        },
        storyTitleRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
        },
        storyInput: {
            flex: 1,
            padding: '0.5rem',
            border: '1px solid #ccc',
            borderRadius: '4px',
        },
        suggestionActions: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '1rem',
        },
        suggestButton: {
            padding: '0.6rem 1.2rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'background-color 0.2s',
        },
        confluenceToggle: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
        },
        checkbox: {
            width: '1rem',
            height: '1rem',
        },
        checkboxLabel: {
            fontSize: '0.9rem',
            color: '#333',
        },
        loader: {
            marginTop: '1rem',
            fontSize: '0.9rem',
            color: '#555',
        },
        storiesContainer: {
            marginTop: '1.5rem',
        },
        storiesHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
        },
        storiesTitle: {
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: 0,
        },
        storyActions: {
            display: 'flex',
            gap: '0.5rem',
        },
        storyActionButton: {
            padding: '0.4rem 0.8rem',
            backgroundColor: '#0052cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.9rem',
            fontWeight: '600',
            transition: 'background-color 0.2s',
        },
        storyCard: {
            padding: '1rem',
            border: '1px solid #ddd',
            borderRadius: '4px',
            marginBottom: '1rem',
            textAlign: 'left',
        },
        storySummary: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginBottom: '0.5rem',
        },
        storyCheckbox: {
            width: '1rem',
            height: '1rem',
        },
        storyDetails: {
            paddingLeft: '1.5rem',
            textAlign: 'left',
        },
        criteriaList: {
            listStyleType: 'disc',
            paddingLeft: '1.5rem',
            marginTop: '0.5rem',
            textAlign: 'left',
        },
        criterion: {
            marginBottom: '0.25rem',
        },
        resultTitle: {
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: 0,
        },
        link: {
            color: '#0052cc',
            textDecoration: 'none',
        },
        storyList: {
            paddingLeft: '1.5rem',
            marginTop: '0.5rem',
        },
        resetButton: {
            marginTop: '1rem',
            padding: '0.6rem 1.2rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
            transition: 'background-color 0.2s',
        },
        buttonGroup: {
            display: 'flex',
            gap: '1rem',
            marginTop: '1rem',
        },
        buttonPrimary: {
            padding: '0.8rem 1.5rem',
            backgroundColor: '#0052cc',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
        },
        buttonSecondary: {
            padding: '0.8rem 1.5rem',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '600',
        },
        suggestionsContainer: {
            marginTop: '2rem',
            textAlign: 'left',
        },
        suggestionsHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '1rem',
        },
        suggestionsTitle: {
            fontSize: '1.2rem',
            fontWeight: '600',
            margin: 0,
            textAlign: 'left',
        },
        selectAllContainer: {
            display: 'flex',
            alignItems: 'center',
        },
        storyHeader: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
        },
        storyTitle: {
            margin: 0,
            fontSize: '1rem',
            textAlign: 'left',
            flex: 1,
            marginLeft: 0,
            marginRight: 'auto',
        },
        toggleButton: {
            width: '32px',
            height: '32px',
            padding: '0',
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '14px',
        },
        removeStoryButton: {
            width: '32px',
            height: '32px',
            padding: '0',
            backgroundColor: 'transparent',
            color: '#333',
            border: '1px solid #ccc',
            borderRadius: '4px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '16px',
            transition: 'all 0.2s',
        },
        addButton: {
            padding: '0.5rem 1rem',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
        },
        removeButton: {
            padding: '0.25rem 0.5rem',
            backgroundColor: '#dc3545',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '0.875rem',
        },
    };